import { Shield, Phone, AlertTriangle, Users, Home, FileText } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const CPSDefense = () => {
  return (
    <GenericPracticePage
      title="CPS Defense Attorney"
      subtitle="Aggressive defense against Child Protective Services investigations and removal proceedings. Protecting your parental rights and keeping your family together."
      icon={<Shield className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Texas CPS Defense Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Few things are more terrifying than having Child Protective Services (CPS) investigate you or remove your children from your home. CPS has tremendous power, and even baseless accusations can lead to investigation, removal, and termination proceedings. If CPS has contacted you or taken your children, you need immediate legal representation.
            </p>
            <p className="text-lg text-muted-foreground">
              We defend parents against false allegations, fight removals, and work to reunify families. Your parental rights are at stake - don't face CPS alone.
            </p>
          </section>

          <section className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
            <AlertTriangle className="h-10 w-10 text-destructive mb-4" />
            <h2 className="text-2xl font-bold mb-4">CPS Investigation or Removal? Call Immediately</h2>
            <p className="text-muted-foreground mb-4">
              <strong>CPS cases move FAST.</strong> You may have only days before critical hearings that determine whether you keep your children. Early legal intervention is crucial.
            </p>
            <p className="text-muted-foreground">
              Do NOT speak to CPS without an attorney present. Anything you say can and will be used against you in court.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">How CPS Cases Work in Texas</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">1</div>
                <div>
                  <h3 className="font-bold mb-1">Investigation</h3>
                  <p className="text-sm text-muted-foreground">CPS receives a report (often anonymous) and investigates. They may interview your children at school without your knowledge or consent. They may visit your home and request entry.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">2</div>
                <div>
                  <h3 className="font-bold mb-1">Emergency Removal (if serious risk)</h3>
                  <p className="text-sm text-muted-foreground">If CPS believes children are in immediate danger, they can remove them from your home WITHOUT a court order. You'll be notified after the fact.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">3</div>
                <div>
                  <h3 className="font-bold mb-1">Adversary Hearing (within 14 days)</h3>
                  <p className="text-sm text-muted-foreground">If children are removed, a hearing must be held within 14 days to determine if removal was justified and what happens next. THIS IS CRITICAL.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">4</div>
                <div>
                  <h3 className="font-bold mb-1">Service Plan</h3>
                  <p className="text-sm text-muted-foreground">CPS creates a "Family Service Plan" with requirements you must complete (counseling, parenting classes, drug tests, etc.) to get your children back.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">5</div>
                <div>
                  <h3 className="font-bold mb-1">Status Hearings</h3>
                  <p className="text-sm text-muted-foreground">Regular court hearings to review your progress and determine when/if children can return home.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center font-bold text-accent">6</div>
                <div>
                  <h3 className="font-bold mb-1">Final Hearing (within 12 months)</h3>
                  <p className="text-sm text-muted-foreground">Within one year, the court must decide: reunification, termination of parental rights, or permanent placement with relative.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Common CPS Allegations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Physical Abuse</h3>
                <p className="text-sm text-muted-foreground">Allegations of hitting, bruising, or injuring a child. Often based on misinterpreted injuries or false reports.</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Neglect</h3>
                <p className="text-sm text-muted-foreground">Claims of inadequate supervision, food, shelter, clothing, or medical care.</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Sexual Abuse</h3>
                <p className="text-sm text-muted-foreground">Extremely serious allegations often based on children's statements or concerning behavior.</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Substance Abuse</h3>
                <p className="text-sm text-muted-foreground">Drug or alcohol use that allegedly affects parenting ability or creates dangerous environment.</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Domestic Violence</h3>
                <p className="text-sm text-muted-foreground">Family violence in the home, even if not directed at children.</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Mental Health Issues</h3>
                <p className="text-sm text-muted-foreground">Allegations that parent's mental health prevents safe caregiving.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Your Rights During CPS Investigation</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">✓</span>
                  <span className="text-sm text-muted-foreground"><strong>Right to refuse entry</strong> - CPS cannot enter your home without your consent, a court order, or exigent circumstances (emergency)</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">✓</span>
                  <span className="text-sm text-muted-foreground"><strong>Right to remain silent</strong> - You don't have to answer questions without a lawyer present</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">✓</span>
                  <span className="text-sm text-muted-foreground"><strong>Right to an attorney</strong> - Get a lawyer immediately; court will appoint one if you can't afford it</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">✓</span>
                  <span className="text-sm text-muted-foreground"><strong>Right to visit your children</strong> - If removed, you're entitled to visitation (though it may be supervised)</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">✓</span>
                  <span className="text-sm text-muted-foreground"><strong>Right to challenge removal</strong> - You can fight to get your children back at hearings</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-accent font-bold">✓</span>
                  <span className="text-sm text-muted-foreground"><strong>Right to appeal</strong> - Unfavorable decisions can be appealed</span>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">How We Defend Against CPS</h2>
            <div className="space-y-6">
              <div className="bg-gradient-subtle p-6 rounded-lg">
                <FileText className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Immediate Action</h3>
                <p className="text-sm text-muted-foreground">
                  We intervene immediately to protect your rights, prepare for the adversary hearing, and work to prevent removal or secure quick return of your children.
                </p>
              </div>

              <div className="bg-gradient-subtle p-6 rounded-lg">
                <Home className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Challenge Evidence</h3>
                <p className="text-sm text-muted-foreground">
                  We scrutinize CPS's evidence, challenge unreliable statements, expose false accusations, and present evidence showing you're a fit parent.
                </p>
              </div>

              <div className="bg-gradient-subtle p-6 rounded-lg">
                <Users className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Family Placement</h3>
                <p className="text-sm text-muted-foreground">
                  If removal is unavoidable, we fight for placement with family members rather than foster care, and work toward reunification.
                </p>
              </div>

              <div className="bg-gradient-subtle p-6 rounded-lg">
                <Shield className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Service Plan Advocacy</h3>
                <p className="text-sm text-muted-foreground">
                  We ensure the service plan is fair and achievable, help you complete requirements, and hold CPS accountable for their obligations.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-destructive">Termination of Parental Rights</h2>
            <p className="text-muted-foreground mb-4">
              The ultimate goal of some CPS cases is termination of parental rights - permanently severing the legal relationship between parent and child. This is IRREVERSIBLE.
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>CPS must prove two things:</strong>
            </p>
            <ol className="list-decimal ml-6 space-y-2 text-muted-foreground mb-4">
              <li>You engaged in specific conduct (abuse, neglect, abandonment, etc.) as listed in Texas Family Code</li>
              <li>Termination is in the best interest of the child</li>
            </ol>
            <p className="text-muted-foreground font-semibold">
              This is the fight of your life. You need an experienced attorney who won't back down.
            </p>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">CPS Contacted You? Act Now.</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Don't wait. Call us immediately for aggressive CPS defense. Your children and your parental rights are at stake.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default CPSDefense;
